import React, { useState } from "react";
import { toast } from "react-toastify";
import { Request } from "../../../hooks/Request";
import { requestAuth } from "../../../components/services/RequestService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import {
  ReportPdfHeader,
  ReportPdfFooter,
  formatGeneratedAt,
} from "./reportBranding";
import {
  addReportBranding,
  styleHeaderRow,
  applyZebraFill,
  finalizeReportSheet,
  addExcelClosingNote,
} from "./reportExcelBranding";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return dateStr.split("T")[0].split("-").reverse().join("/");
  } catch (e) {
    return "-";
  }
};

const styles = StyleSheet.create({
  landscapePage: {
    padding: 20,
    paddingBottom: 40,
    fontSize: 7,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    flexDirection: "column",
  },
  table: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 3,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    minHeight: 24,
    alignItems: "center",
  },
  tableRowAlt: { backgroundColor: "#f8fafc" },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#1e3a8a",
    color: "#ffffff",
    alignItems: "center",
    minHeight: 28,
  },
  tableColHeader: {
    paddingHorizontal: 4,
    paddingVertical: 6,
    borderRightWidth: 0.5,
    borderRightColor: "#3b5aa8",
  },
  tableCellHeader: {
    fontSize: 6,
    fontWeight: "bold",
    color: "#ffffff",
    textAlign: "center",
  },
  tableCol: {
    paddingHorizontal: 4,
    paddingVertical: 6,
    borderRightWidth: 0.5,
    borderRightColor: "#e2e8f0",
  },
  tableCell: { fontSize: 6, color: "#334155", textAlign: "center" },
});

const COLUMNS = [
  { key: "nroImportacion", label: "N° IMPORTACION", width: "5%" },
  { key: "proveedor", label: "PROVEEDOR", width: "7%" },
  { key: "producto", label: "PRODUCTO", width: "8%" },
  { key: "puertoEmbarque", label: "PUERTO DE EMBARQUE (POL)", width: "6%" },
  { key: "puertoDestino", label: "PUERTO DE DESTINO (POD)", width: "6%" },
  { key: "pesoBruto", label: "PESO BRUTO (KG)", width: "5%" },
  { key: "naviera", label: "NAVIERA", width: "6%" },
  {
    key: "cantContenedores",
    label: "CANTIDAD Y TIPO DE CONTENEDORES",
    width: "8%",
  },
  {
    key: "fechaDevolucion",
    label: "FECHA DEVOLUCION CONTENEDORES",
    width: "7%",
  },
  { key: "noBl", label: "N° BL", width: "6%" },
  { key: "etd", label: "ETD", width: "5%" },
  { key: "eta", label: "ETA", width: "5%" },
  { key: "diasTransito", label: "DIAS TRANSITO MARITIMO", width: "6%" },
  { key: "transporteTerrestre", label: "TRANSPORTE TERRESTRE", width: "6%" },
  { key: "modalidadDespacho", label: "MODALIDAD DESPACHO", width: "6%" },
  { key: "almacen", label: "ALMACEN", width: "5%" },
  { key: "fechaArriboAlmacen", label: "FECHA ARRIBO ALMACEN", width: "5%" },
];

const ReportArrivalPDF = ({ data }) => {
  const today = formatGeneratedAt();

  return (
    <Document>
      <Page size="LEGAL" orientation="landscape" style={styles.landscapePage}>
        <ReportPdfHeader
          title="Reporte de Arribo de Importaciones"
          totalRecords={data.length}
          generatedAt={today}
        />
        <View style={styles.table}>
          <View style={styles.tableHeaderRow} fixed>
            {COLUMNS.map((col, idx) => (
              <View
                key={idx}
                style={[styles.tableColHeader, { width: col.width }]}
              >
                <Text style={styles.tableCellHeader}>{col.label}</Text>
              </View>
            ))}
          </View>
          {data.map((row, i) => (
            <View
              style={[styles.tableRow, i % 2 === 0 ? {} : styles.tableRowAlt]}
              key={i}
              wrap={false}
            >
              {COLUMNS.map((col, idx) => (
                <View key={idx} style={[styles.tableCol, { width: col.width }]}>
                  <Text style={styles.tableCell}>{row[col.key] || "-"}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
        <ReportPdfFooter />
      </Page>
    </Document>
  );
};

export const ReportArrivalModal = ({ onClose }) => {
  const [filters, setFilters] = useState({
    mesLlegada: "",
    tipoImportacion: "",
    proveedor: "",
    nroOperacion: "",
  });
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Obtener opciones de los dropdowns
  const { data: typesImport } = Request({
    urlApi: "/Dropdown/types-import/options?PageNumber=1",
  });
  const { data: providers } = Request({
    urlApi: "/Dropdown/providers/options?PageNumber=1",
  });

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const fieldNames = [
        "codigoDeInterno",
        "proveedor",
        "producto",
        "descripciónGeneralDeLaCompra",
        "puertoDeEmbarque(Pol)",
        "pol",
        "puertoDeDestino(Pod)",
        "pod",
        "puertoDeLlegada",
        "cantidadKg",
        "pesoKg",
        "pesoBruto",
        "naviera",
        "cantidad",
        "cantidadContenedores",
        "tamanoContenedor",
        "fechaDevoluciónContenedores",
        "fechaLimiteDevolucionContenedores",
        "n°Bl",
        "billOfLading",
        "etd",
        "eta",
        "diasTransitoMaritimo",
        "díasMarítimos",
        "transporteTerrestre",
        "régimen/PatrónImportación",
        "modalidadDeCarga",
        "modalidadTransporte",
        "almacénDestino",
        "almacen",
        "almacenDescarguio",
        "fechaDeLlegadaAAlmacen",
        "tipoDeImportacion",
      ]
        .map((f) => `Fieldnames=${f}`)
        .join("&");

      const url = `/Procedure/pag?${fieldNames}&IsPaginado=false`;

      const response = await requestAuth("get", url);
      let data = response.data?.data || response.data || [];

      // Filtrar localmente según lo seleccionado
      if (filters.tipoImportacion) {
        data = data.filter(
          (item) => item.tipoDeImportacion === filters.tipoImportacion,
        );
      }
      if (filters.proveedor) {
        data = data.filter((item) => item.proveedor === filters.proveedor);
      }
      if (filters.mesLlegada) {
        data = data.filter(
          (item) =>
            item.fechaDeLlegadaAAlmacen &&
            item.fechaDeLlegadaAAlmacen.startsWith(filters.mesLlegada),
        );
      }
      if (filters.nroOperacion) {
        data = data.filter((item) =>
          item.codigoDeInterno
            ?.toLowerCase()
            .includes(filters.nroOperacion.toLowerCase()),
        );
      }

      setReportData(data);
      toast.success("Reporte generado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const getFlattenedData = () => {
    return reportData.map((item) => {
      const etdDate = item.etd ? new Date(item.etd) : null;
      const etaDate = item.eta ? new Date(item.eta) : null;
      const diasTransito =
        etdDate && etaDate
          ? Math.round((etaDate - etdDate) / (1000 * 60 * 60 * 24))
          : item.diasTransitoMaritimo || item.díasMarítimos || "";

      return {
        nroImportacion: item.codigoDeInterno || "",
        proveedor: item.proveedor || "",
        producto: item.producto || item.descripciónGeneralDeLaCompra || "",
        puertoEmbarque: item["puertoDeEmbarque(Pol)"] || item.pol || "",
        puertoDestino:
          item["puertoDeDestino(Pod)"] || item.pod || item.puertoDeLlegada || "",
        pesoBruto: item.pesoBruto || item.cantidadKg || item.pesoKg || "",
        naviera: item.naviera || "",
        cantContenedores:
          item.cantidad ||
          [item.cantidadContenedores, item.tamanoContenedor]
            .filter(Boolean)
            .join(" ") ||
          "",
        fechaDevolucion: formatDate(
          item.fechaDevoluciónContenedores ||
            item.fechaLimiteDevolucionContenedores,
        ),
        noBl: item["n°Bl"] || item.billOfLading || "",
        etd: formatDate(item.etd),
        eta: formatDate(item.eta),
        diasTransito: diasTransito !== "" ? String(diasTransito) : "",
        transporteTerrestre: item.transporteTerrestre || "",
        modalidadDespacho:
          item["régimen/PatrónImportación"] ||
          item.modalidadDeCarga ||
          item.modalidadTransporte ||
          "",
        almacen:
          item.almacénDestino || item.almacen || item.almacenDescarguio || "",
        fechaArriboAlmacen: formatDate(item.fechaDeLlegadaAAlmacen),
      };
    });
  };

  const handleDownloadExcel = async () => {
    if (!reportData || reportData.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }
    const data = getFlattenedData();
    const generatedAt = formatGeneratedAt();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("REPORTE_ARRIBO_IMPORTACIONES"); // Límite en Excel son 31 caracteres

    worksheet.columns = COLUMNS.map((col) => ({ key: col.key, width: 20 }));

    const headerRow = await addReportBranding(workbook, worksheet, {
      title: "Reporte de Arribo de Importaciones",
      colCount: COLUMNS.length,
      totalRecords: data.length,
      generatedAt,
    });

    worksheet.getRow(headerRow).values = COLUMNS.map((col) => col.label);
    styleHeaderRow(worksheet.getRow(headerRow));

    data.forEach((row, idx) => {
      const excelRow = worksheet.addRow(row);
      applyZebraFill(excelRow, idx % 2 !== 0);
    });

    const lastDataRow = headerRow + data.length;
    finalizeReportSheet(worksheet, {
      headerTopRow: headerRow,
      lastDataRow,
      colCount: COLUMNS.length,
    });
    addExcelClosingNote(worksheet, {
      row: lastDataRow + 2,
      colCount: COLUMNS.length,
      generatedAt,
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "REPORTE_DE_ARRIBO_DE_IMPORTACIONES.xlsx");
    toast.success("Excel descargado exitosamente");
  };

  const handleDownloadPDF = async () => {
    if (!reportData || reportData.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }
    toast.info("Generando PDF, por favor espere...");
    try {
      const data = getFlattenedData();
      const doc = <ReportArrivalPDF data={data} />;
      const asPdf = pdf([]);
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "REPORTE_DE_ARRIBO_DE_IMPORTACIONES.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF descargado exitosamente");
    } catch (error) {
      console.error(error);
      toast.error("Error al generar PDF");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl w-[98vw] max-w-[1600px] max-h-[95vh] flex flex-col relative overflow-hidden uppercase">
      {/* Cabecera Fija */}
      <div className="px-4 py-3 md:px-6 md:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex justify-between items-center shrink-0">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 m-0">
          REPORTE DE ARRIBO DE IMPORTACIONES
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <i className="fas fa-times text-xl"></i>
        </button>
      </div>

      {/* Cuerpo Scrolleable */}
      <div className="p-4 md:p-6 overflow-y-auto flex-1 bg-white">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mes Llegada a Almacén
            </label>
            <input
              type="month"
              value={filters.mesLlegada}
              onChange={(e) =>
                setFilters({ ...filters, mesLlegada: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Importación
            </label>
            <select
              value={filters.tipoImportacion}
              onChange={(e) =>
                setFilters({ ...filters, tipoImportacion: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 bg-white"
            >
              <option value="">Todos</option>
              {typesImport?.map((t, i) => (
                <option key={i} value={t.name}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proveedor
            </label>
            <select
              value={filters.proveedor}
              onChange={(e) =>
                setFilters({ ...filters, proveedor: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 bg-white"
            >
              <option value="">Todos</option>
              {providers?.map((p, i) => (
                <option key={i} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              N° IMPORTACION
            </label>
            <input
              type="text"
              placeholder="Ej: 422 RITEX..."
              value={filters.nroOperacion}
              onChange={(e) =>
                setFilters({ ...filters, nroOperacion: e.target.value })
              }
              className="w-full border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-500 bg-white"
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-end items-center mb-6 border-b border-gray-200 pb-4">
          <div className="flex flex-wrap gap-3">
            <button
              className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${!reportData ? "bg-green-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
              onClick={handleDownloadExcel}
              disabled={!reportData || loading}
            >
              <i className="fas fa-file-excel"></i>
              <span>Descargar Excel</span>
            </button>
            <button
              className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${!reportData ? "bg-red-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
              onClick={handleDownloadPDF}
              disabled={!reportData || loading}
            >
              <i className="fas fa-file-pdf"></i>
              <span>Descargar PDF</span>
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              onClick={handleGenerate}
              disabled={loading}
            >
              <i
                className={
                  loading ? "fas fa-spinner fa-spin mr-2" : "fas fa-eye mr-2"
                }
              ></i>
              <span>{loading ? "Cargando..." : "Previsualizar"}</span>
            </button>
          </div>
        </div>

        {reportData && (
          <div className="overflow-x-auto border border-gray-200 rounded-md shadow-sm pb-4">
            <table className="w-full text-xs text-left">
              <thead className="bg-[#1e3a8a] text-white font-bold border-b border-gray-300 shadow-sm">
                <tr>
                  {COLUMNS.map((col, i) => (
                    <th
                      key={i}
                      className="px-3 py-2 whitespace-nowrap text-center"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={COLUMNS.length}
                      className="text-center py-6 text-gray-500"
                    >
                      No se encontraron resultados para los filtros
                      seleccionados.
                    </td>
                  </tr>
                ) : (
                  getFlattenedData().map((item, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-200 hover:bg-blue-50 align-top even:bg-slate-50 transition-colors"
                    >
                      {COLUMNS.map((col, i) => (
                        <td
                          key={i}
                          className="px-3 py-2 whitespace-nowrap text-center text-gray-700"
                        >
                          {item[col.key] || "-"}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pie Fijo con Botón de Cerrar */}
      <div className="px-4 py-3 md:px-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-2 shrink-0">
        <button
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-medium shadow-sm"
          onClick={onClose}
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};
